/**
 * The two route helpers that answer a URL nobody defined a route for.
 *
 * **Why they live here rather than in `App.tsx`.** They used to be defined
 * there and copied verbatim into the redirect test, because importing
 * `App` bootstraps a deep provider tree a router test has no use for. The
 * copy carried a comment saying it must stay in lockstep, which is another
 * way of saying nothing enforced it — and it drifted the first time the
 * destination changed (#2175 slice 5, caught in review). One definition,
 * imported by both, is the only version of that promise a reader can
 * trust. The module is deliberately dependency-light so a test can import
 * it without standing up the app.
 */
import { Navigate, useLocation } from 'react-router-dom'
import { useAppContext } from '@/hooks/useAppContext'

/** Catch-all for a URL no route matched.
 *
 * A path that is ALREADY workspace-scoped and still unmatched goes to the
 * active workspace's Runs page. This branch is a loop guard, not a
 * convenience: the other branch prepends `/workspaces/<id>` to whatever it
 * was given, so feeding it an already-prefixed path prepended again on
 * every render — `/workspaces/x/workspaces/x/...` — until the browser gave
 * up. That was the bug (`slack.integration_broken` mailed a deep link that
 * hit it).
 *
 * Anything else gets scoped to the active workspace and re-matched, which
 * is how flat URLs from before workspace scoping still resolve. `/` means
 * "no particular page", so it lands on Runs like the workspace index does.
 */
export function WorkspaceRedirect() {
  const { workspaceId } = useAppContext()
  const location = useLocation()
  if (location.pathname.startsWith('/workspaces/')) {
    return <Navigate to={`/workspaces/${workspaceId}/results`} replace />
  }
  const path = location.pathname === '/' ? '/results' : location.pathname
  return <Navigate to={`/workspaces/${workspaceId}${path}`} replace />
}

/** Drop-in alias-redirect that preserves the sibling search string.
 *
 * Plain `<Navigate to="../foo" replace />` resolves the new path but
 * STRIPS the inbound query string — so an in-flight OAuth callback landing
 * on a legacy alias (`workspace/ci-cd?github=connected`,
 * `workspace/notifications?slackError=...`) would lose the status code the
 * canonical page needs to render its banner.
 *
 * This resolves the target the same way `<Navigate>` does but threads
 * `location.search` through, so someone who clicked an old bookmark or
 * just finished an OAuth round-trip lands on the canonical tab with the
 * banner the canonical URL would have shown (#1153).
 */
export function RedirectWithSearch({ to }: { to: string }) {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}`} replace />
}
